/**
 * SessionLoggerModal — Structured workout logger.
 * Replaces the generic training log with per-lift sets tracking.
 *
 * Props:
 *   open: boolean
 *   program: string
 *   onClose: callback
 *   onSaved: callback after successful save
 */

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dumbbell, X, Plus, Trash2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const COMPOUND_LIFTS = [
  { key: "squat", label: "Squat", emoji: "🏋️" },
  { key: "bench", label: "Bench Press", emoji: "💪" },
  { key: "deadlift", label: "Deadlift", emoji: "🔥" },
  { key: "ohp", label: "Overhead Press", emoji: "🚀" },
  { key: "row", label: "Row", emoji: "🚣" },
  { key: "hiit", label: "HIIT / Sprint", emoji: "⚡" },
  { key: "other", label: "Outro", emoji: "✨" },
];

export default function SessionLoggerModal({ open, program = "keto_fusion", onClose, onSaved }) {
  const [kind, setKind] = useState("squat");
  const [duration, setDuration] = useState(60);
  const [sets, setSets] = useState([{ weight: "", reps: "" }]);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const addSet = () => setSets([...sets, { weight: "", reps: "" }]);
  const removeSet = (i) => setSets(sets.filter((_, idx) => idx !== i));
  const updateSet = (i, key, val) => {
    const next = [...sets];
    next[i][key] = val;
    setSets(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const validSets = sets
        .filter((s) => s.weight && s.reps)
        .map((s) => ({ lift: kind, weight: parseFloat(s.weight), reps: parseInt(s.reps, 10) }));
      const total_reps = validSets.reduce((sum, s) => sum + s.reps, 0);
      const top_weight = validSets.length ? Math.max(...validSets.map((s) => s.weight)) : 0;

      await fetch(`${API}/api/programs/${program}/log/training`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          kind,
          duration_min: duration,
          sets: validSets,
          top_weight,
          total_reps,
        }),
      });
      toast.success(`Treino registado · ${validSets.length} sets`);
      onSaved?.();
      onClose();
    } catch {
      toast.error("Erro ao registar treino");
    } finally {
      setSaving(false);
    }
  };

  const validSets = sets.filter((s) => s.weight && s.reps);
  const totalVolume = validSets.reduce((sum, s) => sum + parseFloat(s.weight) * parseInt(s.reps, 10), 0);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}
        data-testid="session-logger-modal"
      >
        <motion.div
          initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }}
          className="bg-zinc-950 border border-purple-500/40 rounded-3xl p-6 max-w-md w-full max-h-[85vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          style={{ boxShadow: "0 20px 80px rgba(168,85,247,0.15)" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <motion.div animate={{ rotate: [0, 8, -8, 0] }} transition={{ duration: 3, repeat: Infinity }}>
                <Dumbbell className="w-5 h-5 text-purple-400" />
              </motion.div>
              <h3 className="text-lg font-bold text-zinc-100">Registar sessão de treino</h3>
            </div>
            <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid="session-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Kind selector */}
          <label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2 block">Tipo</label>
          <div className="grid grid-cols-4 gap-1.5 mb-4">
            {COMPOUND_LIFTS.map((l) => {
              const active = kind === l.key;
              return (
                <button
                  key={l.key}
                  onClick={() => setKind(l.key)}
                  className="p-2 rounded-lg border text-[10px] uppercase font-bold transition-all"
                  style={{
                    borderColor: active ? "#a855f7" : "#27272a",
                    background: active ? "rgba(168,85,247,0.15)" : "transparent",
                    color: active ? "#c084fc" : "#a1a1aa",
                  }}
                  data-testid={`session-kind-${l.key}`}
                >
                  <div className="text-sm">{l.emoji}</div>
                  <div>{l.label}</div>
                </button>
              );
            })}
          </div>

          {/* Duration */}
          <label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 block">Duração (min)</label>
          <Input
            type="number"
            value={duration}
            onChange={(e) => setDuration(parseInt(e.target.value, 10) || 0)}
            className="bg-zinc-900 border-zinc-800 text-zinc-100 mb-4"
            data-testid="session-duration"
          />

          {/* Sets */}
          <div className="flex items-center justify-between mb-2">
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Sets</label>
            <button
              onClick={addSet}
              className="flex items-center gap-1 text-[10px] uppercase text-purple-400 hover:text-purple-300"
              data-testid="session-add-set"
            >
              <Plus className="w-3 h-3" /> Set
            </button>
          </div>
          <div className="space-y-2 mb-4">
            {sets.map((s, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`session-set-${i}`}>
                <span className="text-[10px] font-mono text-zinc-500 w-6">#{i + 1}</span>
                <Input
                  type="number"
                  placeholder="kg"
                  value={s.weight}
                  onChange={(e) => updateSet(i, "weight", e.target.value)}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 flex-1 h-9"
                  data-testid={`session-weight-${i}`}
                />
                <span className="text-xs text-zinc-500">×</span>
                <Input
                  type="number"
                  placeholder="reps"
                  value={s.reps}
                  onChange={(e) => updateSet(i, "reps", e.target.value)}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 flex-1 h-9"
                  data-testid={`session-reps-${i}`}
                />
                {sets.length > 1 && (
                  <button
                    onClick={() => removeSet(i)}
                    className="text-zinc-600 hover:text-red-400"
                    data-testid={`session-remove-${i}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Summary */}
          {validSets.length > 0 && (
            <div className="p-3 rounded-lg bg-zinc-900/60 border border-zinc-800 mb-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-[10px] uppercase text-zinc-500">Sets</p>
                  <p className="text-sm font-bold text-purple-300">{validSets.length}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-zinc-500">Volume total</p>
                  <p className="text-sm font-bold text-purple-300">{totalVolume.toLocaleString()} kg</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-zinc-500">Top set</p>
                  <p className="text-sm font-bold text-purple-300">
                    {Math.max(...validSets.map((s) => parseFloat(s.weight)))} kg
                  </p>
                </div>
              </div>
            </div>
          )}

          <Button
            onClick={save}
            disabled={saving || validSets.length === 0}
            className="w-full bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white font-bold disabled:opacity-40"
            data-testid="session-save"
          >
            {saving ? "A registar..." : <><Check className="w-4 h-4 mr-1" /> Registar sessão</>}
          </Button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
